import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { axios, useAuth } from '../../context/AuthContext';

// ─── Sabitler ────────────────────────────────────────────────────────────────

const DURUM_LABEL = {
  kurum_ogrencisi: 'Kurum',
  misafir_ogrenci: 'Misafir',
};

const DERS_RENK = {
  'Türkçe':           '#6366f1',
  'Sosyal Bilgiler':  '#f59e0b',
  'Matematik':        '#10b981',
  'Fen Bilimleri':    '#3b82f6',
  'Matematik (AYT)':  '#10b981',
};

const DERS_ORDER = ['Türkçe', 'Sosyal Bilgiler', 'Matematik', 'Matematik (AYT)', 'Fen Bilimleri'];

// ─── Yardımcılar ─────────────────────────────────────────────────────────────

const fmt = (v, dec = 2) => {
  if (v == null || v === '') return '-';
  const n = parseFloat(v);
  return isNaN(n) ? '-' : n.toFixed(dec);
};

const pick = (obj, keys, fb = '-') => {
  for (const k of keys) {
    if (obj?.[k] != null && obj[k] !== '') return obj[k];
  }
  return fb;
};

const formatDate = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  if (isNaN(d)) return '-';
  return d.toLocaleString('tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const getList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.sonuclar)) return payload.data.sonuclar;
  if (Array.isArray(payload?.sonuclar)) return payload.sonuclar;
  return [];
};

const getDetailResults = (payload) => {
  if (Array.isArray(payload?.data?.sonuclar)) return payload.data.sonuclar;
  if (Array.isArray(payload?.sonuclar)) return payload.sonuclar;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const getSavedUploadId = (payload) => {
  const candidates = [payload?.id, payload?._id, payload?.data?.id, payload?.data?._id,
    payload?.kayit?.id, payload?.kayit?._id, payload?.data?.kayit?.id, payload?.data?.kayit?._id,
    payload?.upload?.id, payload?.upload?._id, payload?.data?.upload?.id, payload?.data?.upload?._id,
    payload?.upload_id, payload?.yukleme_id, payload?.data?.upload_id, payload?.data?.yukleme_id,
    payload?.sonuc?.id, payload?.data?.sonuc?.id];
  return candidates.find((id) => id !== undefined && id !== null && id !== '') ?? null;
};

const getWhatsAppStats = (payload) => payload?.data?.result || payload?.data?.ozet || payload?.data || payload?.result || payload?.ozet || payload;

// ─── useWindowWidth ───────────────────────────────────────────────────────────

function useWindowWidth() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return w;
}

function WhatsAppResultSummary({ result }) {
  const summary = result && typeof result === 'object' ? result : {};
  const errors = Array.isArray(summary.errors) ? summary.errors : summary.errors ? [summary.errors] : [];
  return (
    <div role="status" style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
      <strong style={{ display: 'block', marginBottom: 8 }}>WhatsApp gönderim sonucu</strong>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: '0.84rem' }}>
        <span>Kuyruğa alınan: {summary.queued ?? 0}</span>
        <span>Atlanan: {summary.skipped ?? 0}</span>
        <span>Başarısız: {summary.failed ?? 0}</span>
      </div>
      {errors.length > 0 && (
        <ul style={{ margin: '8px 0 0', paddingLeft: 20, color: 'var(--text-muted)', fontSize: '0.82rem' }}>
          {errors.map((error, index) => <li key={index}>{typeof error === 'string' ? error : error?.message || JSON.stringify(error)}</li>)}
        </ul>
      )}
    </div>
  );
}

function WhatsAppSelectionPanel({ sonuclar, selectedStudentIds, onToggleStudent, onToggleAll, sending, onSend, result, onRefresh, onNewUpload }) {
  const [activeFilter, setActiveFilter] = useState('kurum_ogrencisi');
  const institutionRows = useMemo(() => {
    const unique = new Map();
    sonuclar.filter((row) => row.ogrenci_id != null && row.durum === 'kurum_ogrencisi')
      .forEach((row) => unique.set(String(row.ogrenci_id), row));
    return [...unique.values()];
  }, [sonuclar]);
  const guestRows = useMemo(() => sonuclar.filter((row) => row.durum === 'misafir_ogrenci'), [sonuclar]);
  const visibleRows = activeFilter === 'kurum_ogrencisi' ? institutionRows : guestRows;
  const visibleIds = visibleRows.map((row) => String(row.ogrenci_id));
  const allVisibleSelected = activeFilter === 'kurum_ogrencisi' && visibleIds.length > 0 && visibleIds.every((id) => selectedStudentIds.has(id));
  const kurumCount = institutionRows.length;
  const misafirCount = guestRows.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, fontSize: 'clamp(1.35rem,3vw,2rem)', margin: 0 }}>💬 Türkiye Geneli Deneme Sonuçları</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 'clamp(0.95rem,2vw,1.15rem)', margin: '6px 0 0' }}>Sonuçları inceleyip velilere WhatsApp mesajını elle gönderin.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn btn-outline" type="button" onClick={onRefresh} disabled={sending} style={{ minHeight: 48, fontWeight: 800 }}>↻ Yenile</button>
          <button className="btn btn-outline" type="button" onClick={onNewUpload} disabled={sending} style={{ minHeight: 48 }}>Yeni PDF yükle</button>
        </div>
      </div>

      <div style={{ background: 'var(--accent-bg,#eef2ff)', borderRadius: 14, padding: '20px 24px', color: 'var(--text-secondary,#475569)', fontSize: 'clamp(0.9rem,1.6vw,1.05rem)' }}>
        WhatsApp mesajları otomatik gönderilmez. Mesajlar yalnızca öğrencileri seçip gönder düğmesine bastığınızda kuyruğa alınır.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 20 }}>
        <div className="stat-card" style={{ '--stat-color': '#2563eb', padding: '30px 36px', borderRadius: 22, minHeight: 112, boxSizing: 'border-box' }}>
          <div style={{ fontSize: '1.7rem', color: '#2563eb', fontWeight: 500 }}>{kurumCount}</div>
          <div style={{ color: 'var(--text-muted)', fontWeight: 800, fontSize: '0.95rem', textTransform: 'uppercase' }}>KURUM ÖĞRENCİSİ · VELİSİNE GÖNDERİLEBİLİR</div>
        </div>
        <div className="stat-card" style={{ '--stat-color': '#2563eb', padding: '30px 36px', borderRadius: 22, minHeight: 112, boxSizing: 'border-box' }}>
          <div style={{ fontSize: '1.7rem', color: '#ef4444', fontWeight: 500 }}>{misafirCount}</div>
          <div style={{ color: 'var(--text-muted)', fontWeight: 800, fontSize: '0.95rem', textTransform: 'uppercase' }}>MİSAFİR SONUCU · WHATSAPP GÖNDERİLEMEZ</div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className={`btn ${activeFilter === 'kurum_ogrencisi' ? 'btn-primary' : 'btn-outline'}`} type="button" onClick={() => setActiveFilter('kurum_ogrencisi')} style={{ minHeight: 52, borderRadius: 14, fontWeight: 800 }}>
            Kurum öğrencileri ({kurumCount})
          </button>
          <button className={`btn ${activeFilter === 'misafir_ogrenci' ? 'btn-primary' : 'btn-outline'}`} type="button" onClick={() => setActiveFilter('misafir_ogrenci')} style={{ minHeight: 52, borderRadius: 14, fontWeight: 800 }}>
            Misafir sonuçları ({misafirCount})
          </button>
        </div>
        <button className="btn" type="button" onClick={onSend} disabled={sending || selectedStudentIds.size === 0} style={{ minHeight: 56, padding: '0 28px', borderRadius: 14, background: '#ef3340', color: '#fff', fontWeight: 800, fontSize: '1rem', boxShadow: '0 8px 18px rgba(239,51,64,0.18)' }}>
          {sending ? 'Gönderim kuyruğa alınıyor…' : `Seçilenlere sonuç gönder (${selectedStudentIds.size})`}
        </button>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden', borderRadius: 22 }}>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table className="table" style={{ minWidth: 1000, margin: 0 }}>
            <thead><tr>
              <th style={{ width: 52 }}>
                {activeFilter === 'kurum_ogrencisi' && <input type="checkbox" aria-label="Kurum öğrencilerinin tümünü seç" checked={allVisibleSelected} disabled={sending || institutionRows.length === 0} onChange={() => onToggleAll(visibleIds, allVisibleSelected)} />}
              </th>
              <th>ÖĞRENCİ / NO</th><th>SINIF</th><th>TÜR</th><th>TOPLAM NET</th><th>PUAN</th><th>OKUL SIRASI</th><th>GENEL SIRALAMA</th>
            </tr></thead>
            <tbody>
              {visibleRows.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 28 }}>Bu bölümde sonuç bulunmuyor.</td></tr>
              ) : visibleRows.map((row, index) => {
                const key = row.ogrenci_id == null ? null : String(row.ogrenci_id);
                const selected = key != null && selectedStudentIds.has(key);
                return <tr key={key ?? row.id ?? index}>
                  <td>{activeFilter === 'kurum_ogrencisi' && key != null && <input type="checkbox" aria-label={`${row.ad_soyad} öğrencisini seç`} checked={selected} disabled={sending || (!selected && selectedStudentIds.size >= 100)} onChange={() => onToggleStudent(row.ogrenci_id)} />}</td>
                  <td><div style={{ fontWeight: 800 }}>{row.ad_soyad || '-'}</div><div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 3 }}>#{row.ogrenci_no ?? '-'}</div></td>
                  <td>{row.sinif_adi ?? '-'}</td><td>{row.tur ?? '-'}</td><td>{fmt(row.toplam_net)}</td><td style={{ fontWeight: 800 }}>{fmt(row.puan)}</td>
                  <td>{row.okul_sirasi ?? '-'}</td><td>{row.genel_siralama ? `#${Number(row.genel_siralama).toLocaleString('tr-TR')}` : '-'}</td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>
      </div>
      {result && <WhatsAppResultSummary result={result} />}
    </div>
  );
}

// ─── Animasyonlu Yükleme ──────────────────────────────────────────────────────

function UploadAnimation({ fileName }) {
  const steps = [
    { icon: '📄', label: 'PDF okunuyor…' },
    { icon: '🔍', label: 'Öğrenci satırları ayrıştırılıyor…' },
    { icon: '🔗', label: 'Kurum öğrencileri eşleştiriliyor…' },
    { icon: '👀', label: 'Önizleme hazırlanıyor…' },
  ];
  const [step, setStep] = useState(0);
  const [dots, setDots] = useState('');
  useEffect(() => {
    const t1 = setInterval(() => setStep((s) => Math.min(s + 1, steps.length - 1)), 1800);
    const t2 = setInterval(() => setDots((d) => (d.length >= 3 ? '' : d + '.')), 400);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, []);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 16px' }}>
      <div style={{
        background: 'var(--card-bg)', border: '1px solid var(--border-color)',
        borderRadius: 20, padding: '32px 28px', width: '100%', maxWidth: 400,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        boxShadow: '0 8px 40px rgba(0,0,0,0.10)', boxSizing: 'border-box',
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          border: '4px solid var(--border-color)',
          borderTop: '4px solid var(--accent,#6366f1)',
          animation: 'spin 0.9s linear infinite',
        }} />
        <div style={{ marginTop: 20, fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)', textAlign: 'center', wordBreak: 'break-all', width: '100%' }}>
          {fileName}
        </div>
        <div style={{ marginTop: 20, width: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {steps.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: i <= step ? 1 : 0.22, transition: 'opacity 0.5s' }}>
              <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{s.icon}</span>
              <span style={{ fontSize: '0.85rem', color: i === step ? 'var(--accent,#6366f1)' : 'var(--text-muted)', fontWeight: i === step ? 700 : 400, transition: 'color 0.4s' }}>
                {s.label}{i === step ? dots : i < step ? ' ✓' : ''}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Ders Net Bar ─────────────────────────────────────────────────────────────

function DersBar({ label, dogru, yanlis, net, soru, renk }) {
  const pct = soru > 0 ? Math.min(100, (dogru / soru) * 100) : 0;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: renk }}>{label}</span>
        <span style={{ fontSize: '0.76rem', color: 'var(--text-muted)', fontWeight: 500 }}>
          <b style={{ color: 'var(--text-primary)', fontSize: '0.85rem' }}>{fmt(net)}</b> net &nbsp;·&nbsp;
          <span style={{ color: '#10b981' }}>{dogru}D</span> <span style={{ color: '#ef4444' }}>{yanlis}Y</span> {soru ? soru - dogru - yanlis : '-'}B
        </span>
      </div>
      <div style={{ height: 8, borderRadius: 4, background: 'var(--border-color,#e5e7eb)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg,${renk}cc,${renk})`, borderRadius: 4, transition: 'width 0.9s ease' }} />
      </div>
    </div>
  );
}

// ─── Karne Modal ──────────────────────────────────────────────────────────────
// Mobilde: bottom sheet (alttan yukarı kayar)
// Masaüstünde: ekranın ortasında centered dialog

function KarneModal({ row, onClose }) {
  const w = useWindowWidth();
  const isMobile = w < 640;
  const dersler = row?.dersler ?? {};
  const altDersler = row?.alt_dersler ?? {};
  const visibleDersler = DERS_ORDER.filter((d) => dersler[d]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)',
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        padding: isMobile ? 0 : '16px',
        boxSizing: 'border-box',
        animation: 'fadeIn 0.18s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--card-bg,#fff)',
          width: '100%',
          maxWidth: isMobile ? '100%' : 580,
          maxHeight: isMobile ? '93dvh' : 'calc(100dvh - 32px)',
          borderRadius: isMobile ? '22px 22px 0 0' : 18,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          boxSizing: 'border-box',
          boxShadow: isMobile
            ? '0 -8px 40px rgba(0,0,0,0.20)'
            : '0 24px 60px rgba(0,0,0,0.22), 0 0 0 1px rgba(0,0,0,0.06)',
          animation: isMobile ? 'slideUpSheet 0.32s cubic-bezier(.22,1,.36,1)' : 'slideUp 0.25s ease',
        }}
      >
        {/* Handle bar – sadece mobilde */}
        {isMobile && (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 12, paddingBottom: 4 }}>
            <div style={{ width: 44, height: 5, borderRadius: 3, background: 'var(--border-color,#d1d5db)' }} />
          </div>
        )}

        <div style={{ padding: isMobile ? '10px 18px 36px' : '26px 26px 22px' }}>

          {/* ─── Öğrenci başlığı ─── */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 18 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 900, fontSize: isMobile ? '1.12rem' : '1.28rem', color: 'var(--text-primary)', wordBreak: 'break-word', lineHeight: 1.2 }}>
                {row.ad_soyad}
              </div>
              <div style={{ marginTop: 7, display: 'flex', flexWrap: 'wrap', gap: '5px 8px', alignItems: 'center' }}>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{row.sinif_adi ?? '-'}</span>
                {row.ogrenci_no && <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>#{row.ogrenci_no}</span>}
                <Pill
                  label={DURUM_LABEL[row.durum] ?? row.durum}
                  bg={row.durum === 'kurum_ogrencisi' ? '#d1fae5' : '#e0e7ff'}
                  color={row.durum === 'kurum_ogrencisi' ? '#065f46' : '#3730a3'}
                />
                {row.tur && (
                  <Pill
                    label={row.tur}
                    bg={row.tur === 'TYT' ? '#ede9fe' : '#fef3c7'}
                    color={row.tur === 'TYT' ? '#5b21b6' : '#92400e'}
                  />
                )}
              </div>
            </div>
            {!isMobile && (
              <button onClick={onClose} aria-label="Kapat" style={{
                background: 'var(--bg-subtle,#f1f5f9)', border: 'none',
                width: 34, height: 34, borderRadius: '50%', cursor: 'pointer',
                fontSize: '1rem', color: 'var(--text-muted)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>✕</button>
            )}
          </div>

          {/* ─── Stat kutucukları ─── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 22 }}>
            {[
              { label: 'Okul Sırası', value: row.okul_sirasi ?? '-', emoji: '🏫' },
              { label: 'Genel Sıra',  value: row.genel_siralama ? `#${Number(row.genel_siralama).toLocaleString('tr-TR')}` : '-', emoji: '🌍' },
              { label: 'Top. Net',    value: fmt(row.toplam_net), emoji: '📊' },
              { label: row.tur === 'AYT' ? 'SAY Puan' : 'TYT Puan', value: fmt(row.puan), emoji: '🎯' },
            ].map((s) => (
              <div key={s.label} style={{
                background: 'var(--bg-subtle,#f8fafc)',
                border: '1.5px solid var(--border-color,#e5e7eb)',
                borderRadius: 12, padding: '10px 6px', textAlign: 'center',
              }}>
                <div style={{ fontSize: isMobile ? '1rem' : '1.1rem', marginBottom: 3 }}>{s.emoji}</div>
                <div style={{
                  fontSize: 'clamp(0.88rem,3vw,1.1rem)', fontWeight: 900,
                  color: 'var(--text-primary)', lineHeight: 1,
                }}>{s.value}</div>
                <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', fontWeight: 600, marginTop: 4, lineHeight: 1.3 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* ─── Ders netleri ─── */}
          {visibleDersler.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <SectionTitle>📚 Ders Netleri</SectionTitle>
              <div style={{
                background: 'var(--bg-subtle,#f8fafc)',
                border: '1px solid var(--border-color,#e5e7eb)',
                borderRadius: 12, padding: '14px 16px',
              }}>
                {visibleDersler.map((d) => {
                  const info = dersler[d];
                  return (
                    <DersBar key={d} label={d} dogru={info.dogru ?? 0} yanlis={info.yanlis ?? 0}
                      net={info.net} soru={info.soru ?? 40} renk={DERS_RENK[d] ?? '#64748b'} />
                  );
                })}
              </div>
            </div>
          )}

          {/* ─── Alt ders detayları ─── */}
          {Object.keys(altDersler).length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <SectionTitle>🔍 Alt Ders Detayları</SectionTitle>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(138px,1fr))', gap: 8 }}>
                {Object.entries(altDersler).map(([ad, info]) => (
                  <div key={ad} style={{
                    background: 'var(--bg-subtle,#f8fafc)',
                    border: '1px solid var(--border-color,#e5e7eb)',
                    borderRadius: 10, padding: '9px 12px',
                  }}>
                    <div style={{ fontWeight: 700, fontSize: '0.76rem', color: 'var(--text-primary)', marginBottom: 5 }}>{ad}</div>
                    <div style={{ display: 'flex', gap: 8, fontSize: '0.74rem' }}>
                      <span style={{ color: '#10b981', fontWeight: 600 }}>D:{info.d ?? '-'}</span>
                      <span style={{ color: '#ef4444', fontWeight: 600 }}>Y:{info.y ?? '-'}</span>
                      <span style={{ color: 'var(--accent,#6366f1)', fontWeight: 700 }}>N:{fmt(info.net)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ─── Kapat butonu ─── */}
          <button onClick={onClose} style={{
            width: '100%', padding: '13px 0', borderRadius: 12, marginTop: 6,
            background: 'var(--accent,#6366f1)', color: '#fff', border: 'none',
            fontWeight: 700, fontSize: '0.92rem', cursor: 'pointer', letterSpacing: '0.02em',
            transition: 'opacity 0.15s',
          }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.88'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Küçük yardımcı bileşenler ───────────────────────────────────────────────

function Pill({ label, bg, color }) {
  return (
    <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '3px 9px', borderRadius: 99, background: bg, color }}>
      {label}
    </span>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontWeight: 800, fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>
      {children}
    </div>
  );
}

// ─── Öğrenci Kartı (mobile liste görünümü) ───────────────────────────────────

function OgrenciKart({ row, idx, visibleDers, onClick }) {
  const durum = row.durum;
  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--card-bg,#fff)',
        border: '1px solid var(--border-color,#e5e7eb)',
        borderRadius: 14, padding: '13px 15px', cursor: 'pointer',
        display: 'flex', flexDirection: 'column', gap: 9,
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{
          width: 30, height: 30, borderRadius: 8,
          background: 'var(--bg-subtle,#f1f5f9)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.72rem', fontWeight: 800, color: 'var(--text-muted)', flexShrink: 0,
        }}>{idx + 1}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: '0.92rem', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {row.ad_soyad}
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 1 }}>
            {row.sinif_adi ?? '-'}{row.ogrenci_no ? ` · #${row.ogrenci_no}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          <Pill
            label={DURUM_LABEL[durum] ?? durum}
            bg={durum === 'kurum_ogrencisi' ? '#d1fae5' : '#e0e7ff'}
            color={durum === 'kurum_ogrencisi' ? '#065f46' : '#3730a3'}
          />
          {row.tur && (
            <Pill
              label={row.tur}
              bg={row.tur === 'TYT' ? '#ede9fe' : '#fef3c7'}
              color={row.tur === 'TYT' ? '#5b21b6' : '#92400e'}
            />
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
        {visibleDers.map((d) => row.dersler?.[d] ? (
          <span key={d} style={{
            fontSize: '0.7rem', fontWeight: 700, padding: '3px 8px', borderRadius: 7,
            background: `${DERS_RENK[d]}18`, color: DERS_RENK[d],
          }}>
            {d.split(' ')[0]}: {fmt(row.dersler[d].net)}
          </span>
        ) : null)}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 900, color: 'var(--text-primary)' }}>{fmt(row.toplam_net)} net</span>
          {row.puan && <span style={{ fontSize: '0.76rem', fontWeight: 700, color: 'var(--accent,#6366f1)' }}>{fmt(row.puan)}</span>}
          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>›</span>
        </div>
      </div>
    </div>
  );
}

// ─── Sonuç Tablosu ────────────────────────────────────────────────────────────

function SonucTablosu({ sonuclar, filtre, onFiltreChange, title }) {
  const [karneRow, setKarneRow] = useState(null);
  const [search, setSearch] = useState('');
  const w = useWindowWidth();
  const isMobile = w < 768;

  const visibleDers = useMemo(
    () => DERS_ORDER.filter((d) => sonuclar.some((r) => r.dersler?.[d])),
    [sonuclar],
  );

  const filtered = useMemo(() => {
    let list = filtre === 'tum' ? sonuclar : sonuclar.filter((r) => r.durum === filtre);
    if (search.trim()) {
      const q = search.trim().toLocaleLowerCase('tr-TR');
      list = list.filter((r) =>
        String(r.ad_soyad ?? '').toLocaleLowerCase('tr-TR').includes(q) ||
        String(r.ogrenci_no ?? '').includes(q),
      );
    }
    return list;
  }, [sonuclar, filtre, search]);

  return (
    <>
      <div className="card" style={{ padding: isMobile ? '14px 14px' : undefined }}>
        <div style={{ marginBottom: 12 }}>
          <h3 style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, fontSize: '0.98rem', margin: '0 0 2px' }}>{title}</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.76rem', margin: 0 }}>
            {filtered.length} / {sonuclar.length} öğrenci · Tıkla, karne gör
          </p>
        </div>

        {/* Arama */}
        <input
          className="form-input"
          style={{ width: '100%', height: 42, fontSize: '0.85rem', boxSizing: 'border-box', marginBottom: 10 }}
          placeholder="🔍  İsim veya öğrenci no ara…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {/* Filtreler */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          {['tum', 'kurum_ogrencisi', 'misafir_ogrenci'].map((f) => (
            <button key={f}
              className={`btn btn-sm ${filtre === f ? 'btn-primary' : 'btn-outline'}`}
              style={{ flex: 1, minWidth: 0, fontSize: '0.76rem', padding: '0 6px', height: 34 }}
              onClick={() => onFiltreChange(f)}>
              {f === 'tum' ? 'Tümü' : f === 'kurum_ogrencisi' ? '🏫 Kurum' : '🧳 Misafir'}
            </button>
          ))}
        </div>

        {/* Mobil: kart listesi / Masaüstü: tablo */}
        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtered.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '28px 0', fontSize: '0.85rem' }}>Sonuç bulunamadı.</div>
            ) : filtered.map((row, idx) => (
              <OgrenciKart key={row.id ?? idx} row={row} idx={idx} visibleDers={visibleDers} onClick={() => setKarneRow(row)} />
            ))}
          </div>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table className="table" style={{ minWidth: 700 }}>
              <thead>
                <tr>
                  {['#', 'Ad Soyad', 'No', 'Sınıf', 'Durum', 'Tür'].map((h) => (
                    <th key={h} style={{ whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                  {visibleDers.map((d) => (
                    <th key={d} style={{ color: DERS_RENK[d], whiteSpace: 'nowrap' }}>{d.split(' ')[0]}</th>
                  ))}
                  {['Top. Net', 'Puan', 'Okul', 'Genel'].map((h) => (
                    <th key={h} style={{ whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={14} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '24px 0' }}>Sonuç bulunamadı.</td></tr>
                ) : filtered.map((row, idx) => {
                  const durum = row.durum;
                  return (
                    <tr key={row.id ?? idx} style={{ cursor: 'pointer' }} onClick={() => setKarneRow(row)}>
                      <td style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{idx + 1}</td>
                      <td style={{ fontWeight: 800, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{row.ad_soyad}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{row.ogrenci_no ?? '-'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{row.sinif_adi ?? '-'}</td>
                      <td>
                        <span className={`badge ${durum === 'kurum_ogrencisi' ? 'badge-success' : 'badge-info'}`}>
                          {DURUM_LABEL[durum] ?? durum}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: row.tur === 'TYT' ? '#6366f1' : '#f59e0b' }}>
                          {row.tur ?? '-'}
                        </span>
                      </td>
                      {visibleDers.map((d) => (
                        <td key={d} style={{ fontWeight: 600, color: DERS_RENK[d] }}>
                          {row.dersler?.[d] ? fmt(row.dersler[d].net) : '-'}
                        </td>
                      ))}
                      <td style={{ fontWeight: 800 }}>{fmt(row.toplam_net)}</td>
                      <td style={{ fontWeight: 700, color: 'var(--accent)' }}>{fmt(row.puan)}</td>
                      <td>{row.okul_sirasi ?? '-'}</td>
                      <td>{row.genel_siralama ? `#${Number(row.genel_siralama).toLocaleString('tr-TR')}` : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {karneRow && <KarneModal row={karneRow} onClose={() => setKarneRow(null)} />}
    </>
  );
}

// ─── Geçmiş Kayıt Kartı (mobile) ─────────────────────────────────────────────

function GecmisKart({ kayit, active, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: active ? 'var(--accent-bg,#f0f4ff)' : 'var(--card-bg,#fff)',
        border: `1.5px solid ${active ? 'var(--accent,#6366f1)' : 'var(--border-color,#e5e7eb)'}`,
        borderRadius: 14, padding: '13px 15px', cursor: 'pointer',
        display: 'flex', flexDirection: 'column', gap: 9,
        transition: 'border-color 0.15s, background 0.15s',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ fontWeight: 800, fontSize: '0.9rem', color: 'var(--text-primary)', flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
          {pick(kayit, ['baslik', 'title'], 'Başlıksız')}
        </div>
        <Pill
          label={kayit.tur ?? 'TYT'}
          bg={(kayit.tur ?? 'TYT') === 'TYT' ? '#ede9fe' : '#fef3c7'}
          color={(kayit.tur ?? 'TYT') === 'TYT' ? '#5b21b6' : '#92400e'}
        />
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <Pill
          label={`🏫 ${pick(kayit, ['kurum_ogrencisi_sayisi', 'kurumOgrencisiSayisi', 'kurum_ogrenci_sayisi'])} kurum`}
          bg="#d1fae5" color="#065f46"
        />
        <Pill
          label={`🧳 ${pick(kayit, ['misafir_ogrenci_sayisi', 'misafirOgrenciSayisi'])} misafir`}
          bg="#e0e7ff" color="#3730a3"
        />
        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>
          {formatDate(pick(kayit, ['created_at', 'createdAt', 'tarih'], null))}
        </span>
      </div>
    </div>
  );
}

// ─── Ana Sayfa ────────────────────────────────────────────────────────────────

export default function TurkiyeGeneliDenemeSonuclariPage() {
  const { user } = useAuth();
  const w = useWindowWidth();
  const isMobile = w < 768;
  const canView = user?.rol === 'kurucu' || user?.rol === 'rehber_ogretmen';
  const canManage = user?.rol === 'kurucu' || user?.rol === 'rehber_ogretmen';

  const [baslik, setBaslik] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [sonuclar, setSonuclar] = useState([]);
  const [gecmis, setGecmis] = useState([]);
  const [seciliKayit, setSeciliKayit] = useState(null);
  const [detaySonuc, setDetaySonuc] = useState([]);
  const [filtre, setFiltre] = useState('tum');
  const [detayFiltre, setDetayFiltre] = useState('tum');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [detailLoad, setDetailLoad] = useState(false);
  const [whatsAppUploadId, setWhatsAppUploadId] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState(new Set());
  const [confirmingUpload, setConfirmingUpload] = useState(false);
  const [whatsAppSending, setWhatsAppSending] = useState(false);
  const [whatsAppResult, setWhatsAppResult] = useState(null);
  const [initialSelectionLoading, setInitialSelectionLoading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const fileRef = useRef();

  const loadGecmis = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/turkiye-geneli-deneme-sonuclari');
      setGecmis(getList(data));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Geçmiş yüklemeler alınamadı.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    const loadLatestSavedUpload = async () => {
      setInitialSelectionLoading(true);
      try {
        const { data } = await axios.get('/turkiye-geneli-deneme-sonuclari');
        const uploads = getList(data);
        if (cancelled) return;
        setGecmis(uploads);
        const latest = uploads[0];
        const latestId = latest?.id ?? latest?._id;
        if (!latestId) return;
        const { data: detail } = await axios.get(`/turkiye-geneli-deneme-sonuclari/${latestId}`);
        if (cancelled) return;
        setWhatsAppUploadId(latestId);
        setSonuclar(getDetailResults(detail));
        setSeciliKayit(latest);
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.message || 'Kayıtlı deneme sonuçları yüklenemedi.');
      } finally {
        if (!cancelled) {
          setLoading(false);
          setInitialSelectionLoading(false);
        }
      }
    };
    loadLatestSavedUpload();
    return () => { cancelled = true; };
  }, [canView]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!pdfFile) { toast.error('Lütfen bir PDF dosyası seçin.'); return; }
    const formData = new FormData();
    formData.append('pdfFile', pdfFile);
    if (baslik.trim()) formData.append('baslik', baslik.trim());
    setUploading(true);
    setPreviewId(null);
    setWhatsAppUploadId(null);
    setSelectedStudentIds(new Set());
    setWhatsAppResult(null);
    try {
      const { data } = await axios.post('/turkiye-geneli-deneme-sonuclari/pdf-yukle', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const gelenSonuclar = getDetailResults(data);
      setPreviewId(data?.data?.preview_id ?? null);
      setWhatsAppResult(null);
      setSonuclar(gelenSonuclar);
      setSeciliKayit(null);
      setDetaySonuc([]);
      setFiltre('tum');
      toast.success(data?.message ?? 'PDF başarıyla yüklendi.');
      setPdfFile(null);
      setBaslik('');
      if (fileRef.current) fileRef.current.value = '';
      await loadGecmis();
    } catch (err) {
      toast.error(err.response?.data?.message || 'PDF yüklenemedi.');
    } finally { setUploading(false); }
  };

  const handleConfirmUpload = async () => {
    if (!previewId || confirmingUpload) return;
    setConfirmingUpload(true);
    try {
      const { data } = await axios.post('/turkiye-geneli-deneme-sonuclari/onayla', { preview_id: previewId });
      const savedId = getSavedUploadId(data);
      if (data?.success === false || !savedId) throw new Error(data?.message || 'Sonuç yüklemesi kaydedilemedi.');
      setWhatsAppUploadId(savedId);
      setPreviewId(null);
      const savedResults = getDetailResults(data);
      if (savedResults.length > 0) setSonuclar(savedResults);
      setSelectedStudentIds(new Set());
      toast.success(data?.message || 'Sonuçlar kaydedildi. Mesaj göndermek için veli listesini seçin.');
      await loadGecmis();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Sonuçlar kaydedilemedi.');
    } finally { setConfirmingUpload(false); }
  };

  const toggleStudentSelection = (studentId) => {
    if (studentId == null) return;
    const key = String(studentId);
    if (!selectedStudentIds.has(key) && selectedStudentIds.size >= 100) {
      toast.error('Tek seferde en fazla 100 öğrenci seçebilirsiniz.');
      return;
    }
    setSelectedStudentIds((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setWhatsAppResult(null);
  };

  const toggleAllStudentSelection = (studentIds, allSelected) => {
    const ids = studentIds.filter(Boolean);
    if (!allSelected && selectedStudentIds.size + ids.filter((id) => !selectedStudentIds.has(id)).length > 100) {
      toast.error('Tek seferde en fazla 100 öğrenci seçebilirsiniz.');
    }
    setSelectedStudentIds((current) => {
      const next = new Set(current);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => { if (next.size < 100) next.add(id); });
      return next;
    });
    setWhatsAppResult(null);
  };

  const handleSendParentResults = async () => {
    if (!whatsAppUploadId || selectedStudentIds.size === 0) return;
    setWhatsAppSending(true);
    setWhatsAppResult(null);
    try {
      const { data } = await axios.post(`/turkiye-geneli-deneme-sonuclari/${whatsAppUploadId}/velilere-whatsapp`, {
        ogrenci_ids: [...selectedStudentIds].map(Number),
      });
      if (data?.success === false) {
        toast.error(data?.message || 'Velilere sonuç mesajı gönderilemedi.');
        return;
      }
      setWhatsAppResult(getWhatsAppStats(data));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Velilere sonuç mesajı gönderilemedi.');
    } finally { setWhatsAppSending(false); }
  };

  const handleGecmisDetay = async (kayit) => {
    const id = kayit?.id ?? kayit?._id;
    if (!id) return;
    setSeciliKayit(kayit);
    setDetailLoad(true);
    setDetayFiltre('tum');
    try {
      const { data } = await axios.get(`/turkiye-geneli-deneme-sonuclari/${id}`);
      setDetaySonuc(getDetailResults(data));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Detay sonuçları alınamadı.');
      setDetaySonuc([]);
    } finally { setDetailLoad(false); }
  };

  const refreshWhatsAppResults = async () => {
    if (!whatsAppUploadId) return;
    setDetailLoad(true);
    try {
      const { data } = await axios.get(`/turkiye-geneli-deneme-sonuclari/${whatsAppUploadId}`);
      setSonuclar(getDetailResults(data));
      setSelectedStudentIds(new Set());
      setWhatsAppResult(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Kayıtlı sonuçlar yenilenemedi.');
    } finally { setDetailLoad(false); }
  };

  if (!canView) {
    return (
      <div className="card">
        <h2 style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, margin: 0 }}>Yetkisiz Erişim</h2>
        <p style={{ color: 'var(--text-muted)', marginTop: 8 }}>Bu sayfayı görüntüleme yetkiniz bulunmuyor.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 12 : 20 }}>

      {/* Başlık */}
      {!whatsAppUploadId && !initialSelectionLoading && <div>
        <h2 style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, fontSize: 'clamp(1.05rem,4vw,1.4rem)', margin: 0 }}>
          Türkiye Geneli Deneme Sonuçları
        </h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem', margin: '4px 0 0' }}>
          {canManage
            ? 'TYT veya AYT PDF dosyasını yükleyin. Kurum öğrencileri otomatik eşleştirilir.'
            : 'Kurum öğrencilerinin Türkiye geneli deneme sonuçlarını ve geçmiş yüklemelerini inceleyin.'}
        </p>
      </div>}

      {/* Upload formu */}
      {canManage && !whatsAppUploadId && showUploadForm && !uploading && (
        <div className="card" style={{ borderColor: 'var(--accent-border)', padding: isMobile ? '14px' : undefined }}>
          <form onSubmit={handleUpload} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Başlık (opsiyonel)</label>
              <input className="form-input" value={baslik} onChange={(e) => setBaslik(e.target.value)}
                placeholder="Örn: Nisan TG TYT 3"
                style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">PDF Dosyası</label>
              <input ref={fileRef} className="form-input" type="file" accept="application/pdf,.pdf"
                onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)}
                style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
            <button className="btn btn-primary" type="submit"
              style={{ width: '100%', justifyContent: 'center', height: 46, fontSize: '0.92rem' }}>
              📤 PDF Yükle &amp; Analiz Et
            </button>
          </form>
        </div>
      )}

      {canManage && !whatsAppUploadId && showUploadForm && uploading && <UploadAnimation fileName={pdfFile?.name ?? 'PDF'} />}

      {canManage && !whatsAppUploadId && showUploadForm && !uploading && previewId && (
        <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.84rem' }}>Önizlemeyi kontrol edin. Kaydettikten sonra mesaj göndermek istediğiniz kurum öğrencilerini seçebilirsiniz.</p>
          <button className="btn btn-primary" type="button" onClick={handleConfirmUpload} disabled={confirmingUpload}>
            {confirmingUpload ? 'Sonuçlar kaydediliyor…' : 'Sonuçları kaydet'}
          </button>
        </div>
      )}

      {canManage && !whatsAppUploadId && showUploadForm && !uploading && previewId && sonuclar.length > 0 && (
        <SonucTablosu sonuclar={sonuclar} filtre={filtre} onFiltreChange={setFiltre} title="✅ Yükleme Sonuçları" />
      )}
      {canManage && !uploading && whatsAppUploadId && (
        <WhatsAppSelectionPanel
          sonuclar={sonuclar}
          selectedStudentIds={selectedStudentIds}
          onToggleStudent={toggleStudentSelection}
          onToggleAll={toggleAllStudentSelection}
          sending={whatsAppSending}
          onSend={handleSendParentResults}
          result={whatsAppResult}
          onRefresh={refreshWhatsAppResults}
          onNewUpload={() => { setWhatsAppUploadId(null); setSelectedStudentIds(new Set()); setWhatsAppResult(null); setPreviewId(null); setShowUploadForm(true); }}
        />
      )}

      {!whatsAppUploadId && initialSelectionLoading && <div className="page-loader" style={{ minHeight: 240 }}><div className="spinner" /></div>}

      {!whatsAppUploadId && !initialSelectionLoading && !showUploadForm && canManage && gecmis.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p style={{ color: 'var(--text-muted)', marginTop: 0 }}>Henüz kaydedilmiş deneme sonucu yok. PDF yükleyerek başlayın.</p>
          <button className="btn btn-primary" type="button" onClick={() => setShowUploadForm(true)}>PDF yükle</button>
        </div>
      )}

      {!whatsAppUploadId && !initialSelectionLoading && !showUploadForm && canManage && gecmis.length > 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p style={{ color: 'var(--text-muted)', marginTop: 0 }}>Kayıtlı sonuç seçilemedi. Son kayıtları yenileyip tekrar deneyin.</p>
          <button className="btn btn-outline" type="button" onClick={loadGecmis} disabled={loading}>Yenile</button>
        </div>
      )}

      {/* Geçmiş yüklemeler */}
      {!whatsAppUploadId && showUploadForm && <div className="card" style={{ padding: isMobile ? '14px' : undefined }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div>
            <h3 style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, fontSize: '0.98rem', margin: 0 }}>Geçmiş Yüklemeler</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.76rem', margin: '2px 0 0' }}>Tıklayarak detay listesini görüntüleyin.</p>
          </div>
          <button className="btn btn-outline btn-sm" onClick={loadGecmis} disabled={loading} style={{ flexShrink: 0 }}>
            {loading ? '…' : '🔄 Yenile'}
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '24px 0', fontSize: '0.85rem' }}>Yükleniyor…</div>
        ) : gecmis.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '24px 0', fontSize: '0.85rem' }}>Henüz yükleme yapılmamış.</div>
        ) : isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {gecmis.map((kayit, i) => {
              const id = kayit.id ?? kayit._id ?? i;
              const active = seciliKayit && (seciliKayit.id ?? seciliKayit._id) === (kayit.id ?? kayit._id);
              return <GecmisKart key={id} kayit={kayit} active={active} onClick={() => handleGecmisDetay(kayit)} />;
            })}
          </div>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table className="table" style={{ minWidth: 560 }}>
              <thead>
                <tr>
                  {['Başlık', 'Tür', 'Dosya', 'Toplam', 'Kurum', 'Misafir', 'Tarih'].map((h) => (
                    <th key={h} style={{ whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {gecmis.map((kayit, i) => {
                  const id = kayit.id ?? kayit._id ?? i;
                  const active = seciliKayit && (seciliKayit.id ?? seciliKayit._id) === (kayit.id ?? kayit._id);
                  return (
                    <tr key={id} onClick={() => handleGecmisDetay(kayit)}
                      style={{ cursor: 'pointer', background: active ? 'var(--accent-bg,#f0f4ff)' : undefined, transition: 'background 0.2s' }}>
                      <td style={{ fontWeight: 800, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                        {pick(kayit, ['baslik', 'title'], 'Başlıksız')}
                      </td>
                      <td>
                        <span style={{ fontWeight: 700, fontSize: '0.78rem', color: (kayit.tur ?? 'TYT') === 'TYT' ? '#6366f1' : '#f59e0b' }}>
                          {kayit.tur ?? 'TYT'}
                        </span>
                      </td>
                      <td style={{ color: 'var(--text-muted)', fontSize: '0.82rem', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {pick(kayit, ['dosya_adi', 'dosyaAdi', 'fileName', 'filename'])}
                      </td>
                      <td>{pick(kayit, ['toplam_ogrenci', 'toplamOgrenci', 'toplam'])}</td>
                      <td><span className="badge badge-success">{pick(kayit, ['kurum_ogrencisi_sayisi', 'kurumOgrencisiSayisi', 'kurum_ogrenci_sayisi'])}</span></td>
                      <td><span className="badge badge-info">{pick(kayit, ['misafir_ogrenci_sayisi', 'misafirOgrenciSayisi'])}</span></td>
                      <td style={{ color: 'var(--text-muted)', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                        {formatDate(pick(kayit, ['created_at', 'createdAt', 'tarih'], null))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>}

      {/* Seçili kayıt detayı */}
      {seciliKayit && (
        detailLoad ? (
          <div className="card" style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '28px 0' }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%',
              border: '3px solid var(--border-color)', borderTop: '3px solid var(--accent,#6366f1)',
              animation: 'spin 0.9s linear infinite', margin: '0 auto',
            }} />
            <p style={{ marginTop: 12, fontSize: '0.85rem' }}>Detay sonuçları yükleniyor…</p>
          </div>
        ) : (
          <SonucTablosu
            sonuclar={detaySonuc}
            filtre={detayFiltre}
            onFiltreChange={setDetayFiltre}
            title={`📊 ${pick(seciliKayit, ['baslik', 'title'], 'Geçmiş Yükleme')} — Detaylar`}
          />
        )
      )}
    </div>
  );
}

/*
  Global CSS'e eklenecek keyframes:

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes slideUpSheet {
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
  }
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
*/
